import React from "react";
import { connect } from "react-redux";
import language from "../../../language";
import { Table } from "../../Table";
import { DeleteModal, Modal } from "../../Modal";
import { Input, Select } from "../../Input";
import { Button } from "../../Button";
import icons from "../../../constants/icons";
import { getStorage } from "../../../utils/storage";
import departmentQuery from "../../../utils/queries/departmentQuery";
import hospitalQuery from "../../../utils/queries/hospitalQuery";
import { defineRole } from "../../../utils/handleAccessRoles";
import { ROLE_PATIENT } from "../../../constants/ROLES";
import toastMessage from "../../../utils/toastMessage";
import { fetchPatients } from "../../../utils/queries/patient";
import {
  fetchPatientHospitalizations,
  savePatientHospitalization,
  deletePatientHospitalization,
} from "../../../utils/queries/patient-hospitalization";

class PatientHospitalizations extends React.Component {
  state = {
    data: [],
    page: 1,
    limit: 10,
    totalPageCount: 0,
    isLoading: false,
    isSubmitting: false,
    isDeleting: false,
    user: {},
    error: {},
    selectedData: {},
    service: null,
    patient: null,
    hospital: null,
    roomNumber: "",
    bedNumber: "",
    isActive: { label: "Active", value: true },
    hospitalFilter: null,
    isFetchingHospitals: false,
    isFetchingPatients: false,
    delete_value: "",
  };

  async componentDidMount() {
    const user = await getStorage();
    this.setState({ user }, () => this.getData(true));
  }

  getUserHospitalId = () => {
    const hospital = this.state.user?.hospital;
    return hospital?._id || hospital;
  };

  shouldAllowAllHospitals = () => {
    const { user } = this.state;
    const hasHospital = !!this.getUserHospitalId();
    return (
      user?.role === "superAdmin" || (user?.role === "admin" && !hasHospital)
    );
  };

  showHospitalFilter = () => {
    const { user } = this.state;
    return user?.role === "superAdmin" || user?.role === "admin";
  };

  getHospitalQueryId = () => {
    if (this.state.hospitalFilter?.value) {
      return this.state.hospitalFilter.value;
    }

    if (this.shouldAllowAllHospitals()) {
      return undefined;
    }

    return this.getUserHospitalId();
  };

  getData = async (isLoading) => {
    try {
// eslint-disable-next-line no-unused-vars
      const { page, limit, user } = this.state;
      const { defaultLanguage, patientId } = this.props;

      this.setState({ isLoading });

      const hospitalId = this.getHospitalQueryId();
      const { data, count, totalRecords } = await fetchPatientHospitalizations(
        defaultLanguage,
        {
          page,
          limit,
          patient: patientId,
          hospital: hospitalId,
        },
      );

      const normalizedData = (data || []).map((el) => ({
        ...el,
        hospitalizationStatus: el?.isActive ? "active" : "inactive",
        patientName: this.getPatientName(el),
      }));

      this.setState({
        data: normalizedData,
        totalPageCount: count || 0,
        isLoading: false,
      });
    } catch (error) {
      this.setState({ isLoading: false });
    }
  };

  getServices = async (isFetchingServices, q) => {
    try {
      this.setState({ isFetchingServices });
      const data = await departmentQuery(this.props.defaultLanguage, {
        type: "dropdown",
        page: 1,
        limit: 50,
        q,
      });
      this.setState({ isFetchingServices: false });
      return data;
    } catch (error) {
      this.setState({ isFetchingServices: false });
      return [];
    }
  };

  getHospitals = async (isFetchingHospitals, q) => {
    try {
      this.setState({ isFetchingHospitals });
      const data = await hospitalQuery(this.props.defaultLanguage, {
        type: "dropdown",
        page: 1,
        limit: 50,
        q,
        isDirectTransfer: true,
      });
      this.setState({ isFetchingHospitals: false });
      return data || [];
    } catch (error) {
      this.setState({ isFetchingHospitals: false });
      return [];
    }
  };

  getPatients = async (isFetchingPatients, q) => {
    try {
      this.setState({ isFetchingPatients });
      const data = await fetchPatients(this.props.defaultLanguage, {
        type: "dropdown",
        page: 1,
        limit: 50,
        q,
      });
      this.setState({ isFetchingPatients: false });
      return data || [];
    } catch (error) {
      this.setState({ isFetchingPatients: false });
      return [];
    }
  };

  onChangeText = (field, e) => {
    const value = e?.target ? e.target.value : e;
    const error = { ...this.state.error };
    delete error[field];
    this.setState({ [field]: value, error });
  };

  handlePagination = (page) => {
    this.setState({ page }, () => this.getData(true));
  };

  handleHospitalFilterChange = (value) => {
    this.setState({ hospitalFilter: value, page: 1 }, () => this.getData(true));
  };

  handleShowModal = (modal, modalTitle, selectedData = {}) => {
    this.setState({
      [modal]: true,
      modalTitle,
      selectedData,
      service: selectedData?.service
        ? {
            label: selectedData.service.name,
            value: selectedData.service._id,
          }
        : null,
      patient: selectedData?.patient
        ? {
            label: `${selectedData?.patient?.patientId || "-"} - ${
              selectedData?.patient?.firstName || ""
            } ${selectedData?.patient?.lastName || ""} (${
              selectedData?.patient?.patientBBMSNumber || "-"
            })`,
            value: selectedData?.patient?._id,
          }
        : null,
      hospital: selectedData?.hospital
        ? {
            label: selectedData.hospital.name,
            value: selectedData.hospital._id,
          }
        : null,
      roomNumber: selectedData?.roomNumber || "",
      bedNumber: selectedData?.bedNumber || "",
      isActive: {
        label: selectedData?.isActive === false ? "Inactive" : "Active",
        value: selectedData?.isActive !== false,
      },
      error: {},
    });
  };

  handleShowHospitalInfo = (item) => {
    this.setState({
      showHospitalInfoModal: true,
      selectedHospitalInfo: item?.hospital || {},
    });
  };

  handleCloseModal = (modal) => {
    this.setState({
      [modal]: false,
      selectedData: {},
      service: null,
      patient: null,
      hospital: null,
      roomNumber: "",
      bedNumber: "",
      isActive: { label: "Active", value: true },
      error: {},
    });
  };

  validateForm = () => {
    const error = {};
    if (!this.state.service) {
      error.service = language[this.props.defaultLanguage].service_required;
    }
    if (!this.props.patientId && !this.state.patient) {
      error.patient = language[this.props.defaultLanguage].patient_id_required;
    }
    if (this.state?.user?.role === "superAdmin" && !this.state.hospital) {
      error.hospital = language[this.props.defaultLanguage].hospital_required;
    }
    this.setState({ error });
    return Object.keys(error).length === 0;
  };

  onSubmit = async () => {
    const canCreateHospitalization =
      defineRole({
        roles: this.state?.user?.accessRole,
        menu: ROLE_PATIENT,
        operation: "create",
      });
    const canUpdateHospitalization =
      defineRole({
        roles: this.state?.user?.accessRole,
        menu: ROLE_PATIENT,
        operation: "update",
      });
    const isEdit = !!this.state?.selectedData?._id;
    if ((isEdit && !canUpdateHospitalization) || (!isEdit && !canCreateHospitalization)) {
      toastMessage("error", "Not allowed");
      return;
    }

    if (!this.validateForm()) return;
    try {
      this.setState({ isSubmitting: true });
      const {
        user,
        selectedData,
        patient: selectedPatient,
        service,
        hospital: selectedHospital,
        roomNumber,
        bedNumber,
        isActive,
      } = this.state;
      const hospital =
        user?.role === "superAdmin"
          ? selectedHospital?.value
          : user?.hospital?._id || user?.hospital;
      const patient =
        this.props.patientId ||
        selectedPatient?.value ||
        selectedData?.patient?._id ||
        selectedData?.patient;

      await savePatientHospitalization(this.props.defaultLanguage, {
        id: selectedData?._id || undefined,
        patient,
        hospital,
        service: service?.value,
        roomNumber,
        bedNumber,
        isActive: isActive?.value !== false,
      });

      this.setState({ isSubmitting: false });
      this.handleCloseModal("showModal");
      this.getData(true);
    } catch (error) {
      this.setState({ isSubmitting: false });
    }
  };

  onDelete = async () => {
    const canDeleteHospitalization =
      defineRole({
        roles: this.state?.user?.accessRole,
        menu: ROLE_PATIENT,
        operation: "delete",
      });
    if (!canDeleteHospitalization) {
      toastMessage("error", "Not allowed");
      return;
    }

    try {
      this.setState({ isDeleting: true });
      await deletePatientHospitalization(
        this.props.defaultLanguage,
        this.state.selectedData?._id,
      );
      this.setState({ isDeleting: false });
      this.handleCloseModal("showDeleteModal");
      this.getData(true);
    } catch (error) {
      this.setState({ isDeleting: false });
    }
  };

  returnHeaders = () => {
    const baseHeaders = [
      {
        title: language[this.props.defaultLanguage].entity_or_hospital,
        key: "hospital.name",
        type: "link",
        onPress: (item) => this.handleShowHospitalInfo(item),
      },
      {
        title: language[this.props.defaultLanguage].service,
        key: "service.name",
      },
      {
        title: language[this.props.defaultLanguage].room_number,
        key: "roomNumber",
      },
      {
        title: language[this.props.defaultLanguage].bed_number,
        key: "bedNumber",
      },
      {
        title: language[this.props.defaultLanguage].status,
        key: "hospitalizationStatus",
        type: "status",
      },
      {
        title: language[this.props.defaultLanguage].created_at,
        key: "createdAt",
        isMoment: true,
        formatTime: "lll",
      },
      {
        title: language[this.props.defaultLanguage].actions,
        key: "action",
      },
    ];

    if (!this.props.patientId) {
      return [
        {
          title: language[this.props.defaultLanguage].patient,
          key: "patientName",
        },
        ...baseHeaders,
      ];
    }

    return baseHeaders;
  };

  getPatientName = (item = {}) => {
    const first = item?.patient?.firstName || "";
    const last = item?.patient?.lastName || "";
    const fullName = `${first} ${last}`.trim();
    return fullName || "-";
  };

  renderFilters = () => {
    if (!this.showHospitalFilter()) return null;

    return (
      <div className="row mb-3">
        <div className="col-md-6">
          <Select
            loadOptions={(q) => this.getHospitals(true, q)}
            label={language[this.props.defaultLanguage].entity_or_hospital}
            value={this.state.hospitalFilter}
            onChange={this.handleHospitalFilterChange}
            isLoading={this.state.isFetchingHospitals}
          />
        </div>
      </div>
    );
  };

  render() {
    const canReadHospitalization =
      defineRole({
        roles: this.state?.user?.accessRole,
        menu: ROLE_PATIENT,
        operation: "read",
      });
    const canCreateHospitalization =
      defineRole({
        roles: this.state?.user?.accessRole,
        menu: ROLE_PATIENT,
        operation: "create",
      });
    const canUpdateHospitalization =
      defineRole({
        roles: this.state?.user?.accessRole,
        menu: ROLE_PATIENT,
        operation: "update",
      });
    const canDeleteHospitalization =
      defineRole({
        roles: this.state?.user?.accessRole,
        menu: ROLE_PATIENT,
        operation: "delete",
      });

    if (!canReadHospitalization) return null;

    return (
      <>
        {this.renderFilters()}
        <Table
          data={this.state.data}
          page={this.state.page}
          limit={this.state.limit}
          totalPageCount={this.state.totalPageCount}
          totalRecords={this.state.totalRecords}
          isLoading={this.state.isLoading}
          headers={this.returnHeaders()}
          showAdd={canCreateHospitalization}
          addButtonText={language[this.props.defaultLanguage].add}
          handleAddPressed={this.handleShowModal.bind(
            this,
            "showModal",
            language[this.props.defaultLanguage].add,
          )}
          handlePagination={this.handlePagination}
          actions={[
            {
              canAccess: canUpdateHospitalization,
              name: language[this.props.defaultLanguage].edit,
              onPress: this.handleShowModal.bind(
                this,
                "showModal",
                language[this.props.defaultLanguage].edit,
              ),
              icon: icons.edit,
            },
            {
              canAccess: canDeleteHospitalization,
              name: language[this.props.defaultLanguage].delete,
              onPress: this.handleShowModal.bind(
                this,
                "showDeleteModal",
                language[this.props.defaultLanguage].delete,
              ),
              icon: icons.delete,
            },
          ]}
        />

        <Modal
          handleClose={this.handleCloseModal.bind(this, "showModal")}
          show={this.state.showModal}
          title={this.state.modalTitle}
          showHeaderBottomBorder={false}
        >
          <div className="card-body">
            <div className="row">
              {!this.props.patientId && (
                <div className="col-md-12">
                  <Select
                    loadOptions={(q) => this.getPatients(true, q)}
                    label={language[this.props.defaultLanguage].patient}
                    value={this.state.patient}
                    onChange={(e) => this.onChangeText("patient", e)}
                    error={this.state.error.patient}
                    isLoading={this.state.isFetchingPatients}
                    required
                  />
                </div>
              )}
              {this.state?.user?.role === "superAdmin" && (
                <div className="col-md-12">
                  <Select
                    loadOptions={(q) => this.getHospitals(true, q)}
                    label={language[this.props.defaultLanguage].entity_or_hospital}
                    value={this.state.hospital}
                    onChange={(e) => this.onChangeText("hospital", e)}
                    error={this.state.error.hospital}
                    isLoading={this.state.isFetchingHospitals}
                    required
                  />
                </div>
              )}
              <div className="col-md-12">
                <Select
                  loadOptions={(q) => this.getServices(true, q)}
                  label={language[this.props.defaultLanguage].service}
                  value={this.state.service}
                  onChange={(e) => this.onChangeText("service", e)}
                  error={this.state.error.service}
                  isLoading={this.state.isFetchingServices}
                  required
                />
              </div>
              <div className="col-md-6">
                <Input
                  label={language[this.props.defaultLanguage].room_number}
                  value={this.state.roomNumber}
                  onChange={(e) => this.onChangeText("roomNumber", e)}
                />
              </div>
              <div className="col-md-6">
                <Input
                  label={language[this.props.defaultLanguage].bed_number}
                  value={this.state.bedNumber}
                  onChange={(e) => this.onChangeText("bedNumber", e)}
                />
              </div>
              <div className="col-md-12">
                <Select
                  label={language[this.props.defaultLanguage].status}
                  value={this.state.isActive}
                  onChange={(e) => this.onChangeText("isActive", e)}
                  options={[
                    {
                      label: language[this.props.defaultLanguage].active,
                      value: true,
                    },
                    {
                      label: language[this.props.defaultLanguage].inactive,
                      value: false,
                    },
                  ]}
                />
              </div>
            </div>
            <div className="d-flex justify-content-end mt-3">
              <Button
                text={language[this.props.defaultLanguage].submit}
                isSubmitting={this.state.isSubmitting}
                onPress={this.onSubmit}
              />
            </div>
          </div>
        </Modal>

        <Modal
          handleClose={this.handleCloseModal.bind(this, "showDeleteModal")}
          show={this.state.showDeleteModal}
          title={this.state.modalTitle}
        >
          <DeleteModal
            handleCloseModal={this.handleCloseModal.bind(
              this,
              "showDeleteModal",
            )}
            onDelete={this.onDelete}
            isDeleting={this.state.isDeleting}
            name={this.state?.selectedData?._id}
            value={this.state.delete_value}
            onChange={(e) => this.onChangeText("delete_value", e)}
          />
        </Modal>

        <Modal
          handleClose={this.handleCloseModal.bind(this, "showHospitalInfoModal")}
          show={this.state.showHospitalInfoModal}
          title={language[this.props.defaultLanguage].entity_or_hospital}
          showHeaderBottomBorder={false}
        >
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <Input
                  disabled
                  label={language[this.props.defaultLanguage].hospital_name}
                  value={this.state.selectedHospitalInfo?.name || "-"}
                />
              </div>
              <div className="col-md-6">
                <Input
                  disabled
                  label={language[this.props.defaultLanguage].code}
                  value={this.state.selectedHospitalInfo?.code || "-"}
                />
              </div>
              <div className="col-md-6">
                <Input
                  disabled
                  label={language[this.props.defaultLanguage].type}
                  value={this.state.selectedHospitalInfo?.type || "-"}
                />
              </div>
              <div className="col-md-6">
                <Input
                  disabled
                  label={language[this.props.defaultLanguage].stock_name}
                  value={this.state.selectedHospitalInfo?.bloodBank?.name || "-"}
                />
              </div>
              <div className="col-md-12">
                <label className="form-label" style={{ fontWeight: "600", marginBottom: "0.4rem", display: "block" }}>
                  {language[this.props.defaultLanguage].residential_address}
                </label>
                <div style={{ border: "1px solid #dee2e6", borderRadius: "6px", padding: "0.6rem 1rem", background: "#f8f9fa", fontSize: "0.88rem", lineHeight: "2" }}>
                  {[
                    { label: "Province", value: this.state.selectedHospitalInfo?.address?.province },
                    { label: "District", value: this.state.selectedHospitalInfo?.address?.district },
                    { label: "Sector", value: this.state.selectedHospitalInfo?.address?.sector },
                    { label: "Cell", value: this.state.selectedHospitalInfo?.address?.cell },
                    { label: "Village", value: this.state.selectedHospitalInfo?.address?.village },
                  ].map(({ label, value }) => (
                    <div key={label}>
                      <span style={{ color: "#6c757d" }}>{label}:</span>{" "}
                      <span style={{ fontWeight: "500" }}>{value || "—"}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Modal>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  return { defaultLanguage };
};

export default connect(mapStateToProps)(PatientHospitalizations);
